import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest } from '@/services/Request';
import { AppCard } from '@/components/ui/AppCard';
import { Skeleton } from '@/components/ui/Skeleton';
import * as jose from 'jose';
import {
  User,
  Mail,
  Shield,
  Clock,
  BookOpen,
  MessageSquare,
  Heart,
  Plus,
  Activity,
} from 'lucide-react';

// Decode the current admin info from the JWT token
function getAdminInfo() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const decoded: any = jose.decodeJwt(token);
    return decoded;
  } catch {
    return null;
  }
}

// Map action types to human-readable labels and icons
const ACTION_MAP: Record<string, { label: string; icon: typeof Activity; color: string }> = {
  SEND_MESSAGE: { label: 'Sent Message', icon: MessageSquare, color: 'text-blue-500 bg-blue-50' },
  CREATE_CHAT: { label: 'Created Chat', icon: Plus, color: 'text-indigo-500 bg-indigo-50' },
  ADD_BOOK: { label: 'Added Book', icon: BookOpen, color: 'text-emerald-500 bg-emerald-50' },
  LIKE_BOOK: { label: 'Liked Book', icon: Heart, color: 'text-rose-500 bg-rose-50' },
};

function getActionInfo(actionType: string) {
  return ACTION_MAP[actionType] || { label: actionType, icon: Activity, color: 'text-coal-500 bg-coal-50' };
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Settings() {
  const adminInfo = getAdminInfo();

  // Fetch user activities — calling without an ID gets current user's activity
  const { data: activities, isLoading: activitiesLoading } = useQuery({
    queryKey: [endpoints.getUserActivities],
    queryFn: () => axiosGetRequest(endpoints.getUserActivities),
    select: data => data.result || data.data || [],
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-coal-900">Settings</h1>
      </div>

      {/* Admin Profile Card */}
      <AppCard>
        <div className="flex items-start gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-2xl font-bold text-white shadow-sm">
            {adminInfo?.fullName?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-coal-900">{adminInfo?.fullName || 'Admin'}</h2>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
              {adminInfo?.email && (
                <div className="flex items-center gap-1.5 text-sm text-coal-500">
                  <Mail className="h-3.5 w-3.5" />
                  <span>{adminInfo.email}</span>
                </div>
              )}
              {adminInfo?.role && (
                <div className="flex items-center gap-1.5 text-sm text-coal-500">
                  <Shield className="h-3.5 w-3.5" />
                  <span className="capitalize">{adminInfo.role}</span>
                </div>
              )}
              {adminInfo?._id && (
                <div className="flex items-center gap-1.5 text-sm text-coal-400">
                  <User className="h-3.5 w-3.5" />
                  <span className="font-mono text-xs">{adminInfo._id}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </AppCard>

      {/* Activity Feed */}
      <AppCard>
        <div className="mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold text-coal-900">Your Activity</h3>
          {activities?.length > 0 && (
            <span className="ml-auto rounded-full bg-coal-100 px-2.5 py-0.5 text-xs font-medium text-coal-600">
              {activities.length} activities
            </span>
          )}
        </div>

        {activitiesLoading ? (
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-3/4 rounded" />
                  <Skeleton className="h-3 w-1/2 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : activities?.length > 0 ? (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[18px] top-0 bottom-0 w-px bg-coal-100" />

            <div className="space-y-0">
              {activities.map((activity: any, index: number) => {
                const actionInfo = getActionInfo(activity.actionType);
                const Icon = actionInfo.icon;

                return (
                  <div
                    key={activity._id || index}
                    className="group relative flex items-start gap-3 rounded-lg px-1 py-2.5 transition-colors hover:bg-coal-50/50"
                  >
                    {/* Icon */}
                    <div className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${actionInfo.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium text-coal-800">
                          {actionInfo.label}
                        </span>
                        <span className="flex shrink-0 items-center gap-1 text-xs text-coal-400">
                          <Clock className="h-3 w-3" />
                          {formatDate(activity.createdAt)}
                        </span>
                      </div>

                      {/* Description or meta */}
                      {activity.description && (
                        <p className="mt-0.5 text-sm text-coal-500">{activity.description}</p>
                      )}
                      {activity.meta?.content && !activity.description && (
                        <p className="mt-0.5 truncate text-sm text-coal-400">
                          "{activity.meta.content}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-coal-100">
              <Activity className="h-6 w-6 text-coal-400" />
            </div>
            <p className="text-sm font-medium text-coal-600">No activity yet</p>
            <p className="mt-1 text-xs text-coal-400">Your recent actions will appear here</p>
          </div>
        )}
      </AppCard>
    </div>
  );
}

export default Settings;
